import {wallDistance,wallLength,wallIntersectsSphere,wallIntersectsSweep,type PaintedWallGeometry,type WallPoint} from './painted-wall-geometry';

export interface PaintedWallSection {
  index: number;
  startFt: number;
  endFt: number;
  geometry: PaintedWallGeometry;
}

/** Geometry-only interval selection. The native cast planner owns inventory materialization.
 * Repeated trajectory crossings touch each interval once.
 */
export function paintedWallSectionsOnPath(wall:PaintedWallGeometry,points:WallPoint[],sectionFt=5):number[]|null {
  if(!Array.isArray(points)||points.length<2||points.length>1024||!Number.isFinite(sectionFt)||sectionFt<=1e-6)return null;
  for(const p of points)if(!p||![p.x,p.y,p.z].every(v=>typeof v==='number'&&Number.isFinite(v)))return null;
  const count=Math.max(1,Math.ceil((wallLength(wall.points)-1e-6)/sectionFt));
  if(!Number.isSafeInteger(count)||count>4096)return null;
  const hits:number[]=[];
  for(let index=0;index<count;index++){
    const section=paintedWallSection(wall,index,sectionFt);if(!section)return null;
    for(let i=1;i<points.length;i++)if(wallIntersectsSweep(section.geometry,points[i-1]!,points[i]!,0,0)){
      hits.push(index);break;
    }
  }
  return hits;
}

/** Broad damage selects distinct cast-local intervals, including overlapping intervals.
 * Null is an invalid/unbounded request, never an empty successful hit selection.
 * Callers must resolve damage against inventory once per returned index.
 */
export function paintedWallSectionsInSphere(wall:PaintedWallGeometry,center:WallPoint,radius:number,sectionFt=5):number[]|null {
  if(![center.x,center.y,center.z,radius,sectionFt].every(Number.isFinite)||radius<0||sectionFt<=1e-6)return null;
  const count=Math.max(1,Math.ceil((wallLength(wall.points)-1e-6)/sectionFt));
  if(!Number.isSafeInteger(count)||count>4096)return null;
  const hits:number[]=[];
  for(let index=0;index<count;index++) {
    const section=paintedWallSection(wall,index,sectionFt);
    if(!section)return null;
    if(wallIntersectsSphere(section.geometry,center,radius))hits.push(index);
  }
  return hits;
}

/** Stable cast-local interval, independent of render tessellation or brush sample density.
 * Inventory owns vitality. This adapter supplies geometry only; it creates no permanent room walls.
 * Like ordinary wall runs, a tail below one microfoot belongs to the previous section.
 * Slice points are derived render/collision geometry, never a new cast wire payload; persist the
 * interval with its parent cast identity, not a separately re-parsed shortened path.
 */
export function paintedWallSection(wall: PaintedWallGeometry,index:number,sectionFt=5): PaintedWallSection|null {
  if(!Number.isSafeInteger(index)||index<0||!Number.isFinite(sectionFt)||sectionFt<=1e-6)return null;
  const length=wallLength(wall.points);
  const count=Math.max(1,Math.ceil((length-1e-6)/sectionFt));
  if(index>=count)return null;
  const startFt=index*sectionFt,endFt=index===count-1?length:(index+1)*sectionFt;
  const points:WallPoint[]=[];let along=0;
  const append=(p:WallPoint)=>{
    const last=points[points.length-1];
    if(!last||wallDistance(last,p)>1e-9)points.push(p);
  };
  for(let i=1;i<wall.points.length;i++) {
    const a=wall.points[i-1]!,b=wall.points[i]!,run=wallDistance(a,b);
    const low=Math.max(startFt,along),high=Math.min(endFt,along+run);
    if(high>low+1e-9) {
      for(const distance of[low,high]) {
        const t=(distance-along)/run;
        append({x:a.x+(b.x-a.x)*t,y:a.y+(b.y-a.y)*t,z:a.z+(b.z-a.z)*t});
      }
    }
    along+=run;if(along>=endFt-1e-9)break;
  }
  return points.length>=2?{index,startFt,endFt,geometry:{...wall,points}}:null;
}
